import React, { useEffect } from 'react';
import { ArrowLeft2, ArrowRight2, GripVertical, InfoCircle } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SECTIONS } from '@/components/wizard/constants';
import { WizardStep } from '@/components/wizard/types';
import { cn } from '@/lib/utils';
import { useReportWizard } from '../context/ReportWizardContext';
import { usePanelTransition } from '../hooks/usePanelTransition';
import { InstrumentNameHeading } from './InstrumentNameHeading';
import { UnsavedReportStepBlock } from './UnsavedReportStepBlock';

interface StepContentSidebarProps {
    sidebarWidth: number;
    isResizing: boolean;
    onRightSidebarResizeStart: (clientX: number) => void;
    isRightSidebarExpanded: boolean;
    isSnappingRightSidebar: boolean;
    setIsRightSidebarCollapsed: (collapsed: boolean) => void;
    currentStep?: WizardStep;
    handleAddBlock: (toolId: string, scope?: { anchorRealId: string | null }) => void;
    disabled?: boolean;
    isInSheet?: boolean;
    onClose?: () => void;
}

export const StepContentSidebar: React.FC<StepContentSidebarProps> = ({
    sidebarWidth,
    isResizing,
    onRightSidebarResizeStart,
    isRightSidebarExpanded,
    isSnappingRightSidebar,
    setIsRightSidebarCollapsed,
    currentStep,
    handleAddBlock,
    disabled = false,
    isInSheet = false,
    onClose,
}) => {
    const {
        formData,
        handleUpdate,
        reportId,
        isLoading,
        sidebarSteps,
        currentStepIndex,
        handleStepClick,
        panelScope,
        setPanelScope,
    } = useReportWizard();
    const CurrentStepComponent = currentStep?.component;

    // Only the tests step drills into block groups. Without a scope the header
    // renders exactly as before, so nothing new appears for the other steps.
    const drilldownScope = currentStep?.id === SECTIONS.TESTS ? panelScope : null;

    // Announce the swap. Suspended while the panel's own width is in motion —
    // resizing, snapping shut, or sitting collapsed — because an 8px content
    // enter riding on a 300ms width transition reads as a wobble, not a cue.
    const { headerRef, bodyRef, scrollRef, cueRef } = usePanelTransition({
        stepId: currentStep?.id ?? null,
        stepIndex: currentStepIndex,
        scope: drilldownScope,
        suspended: isResizing || isSnappingRightSidebar || !isRightSidebarExpanded,
    });

    // `reportId` comes from the URL and is already set before the fetch resolves,
    // so opening an existing report never flashes the block; `isLoading` covers
    // the blank-report bootstrap.
    const isReportUnsaved = !isLoading && !reportId && !formData.id;
    const shouldBlockStep = isReportUnsaved && currentStep?.id !== SECTIONS.PATIENT;
    // The patient step's description explains the identification data, which is
    // what it collects *after* the report exists. Before that it is the choose-a-
    // patient panel, and the description would tooltip about something else.
    const hideStepDescription = isReportUnsaved && currentStep?.id === SECTIONS.PATIENT;

    // Escape is the keyboard twin of the "Voltar" control. It defers to
    // anything that owns Escape more strongly — an open dialog, a text editor,
    // a handler that already consumed the event — so it only fires when nothing
    // else would.
    useEffect(() => {
        if (!drilldownScope) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (document.querySelector('[role="dialog"][data-state="open"]')) return;
            // The inline block editor owns Escape while it is open, and it
            // listens on `document` too — whichever listener registered first
            // wins, so the guard has to be explicit rather than rely on
            // `defaultPrevented`. Otherwise one Escape both closes the editor
            // and drops the panel's scope.
            if (document.querySelector('[data-floating-section-editor]')) return;

            const target = event.target as HTMLElement | null;
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target?.isContentEditable
            ) {
                return;
            }

            event.preventDefault();
            setPanelScope(null);
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [drilldownScope, setPanelScope]);

    const handleChoosePatient = () => {
        const patientStepIndex = sidebarSteps.findIndex((step) => step.id === SECTIONS.PATIENT);
        handleStepClick(Math.max(patientStepIndex, 0));
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
            return;
        }

        setIsRightSidebarCollapsed(true);
    };

    return (
        <div
            id={isRightSidebarExpanded ? 'tour-tools-sidebar' : undefined}
            className={cn(
                'relative z-20 flex flex-shrink-0 flex-col overflow-hidden border-l bg-background',
                isInSheet && 'size-full border-l-0',
                isResizing
                    ? 'transition-none'
                    : isSnappingRightSidebar
                      ? 'ease-[cubic-bezier(0.22,1,0.36,1)] transition-[width] duration-300'
                      : 'transition-[width] duration-300',
                isRightSidebarExpanded ? 'border-border' : 'border-transparent',
            )}
            style={{
                width: isInSheet ? undefined : isRightSidebarExpanded ? `${sidebarWidth}px` : '0px',
            }}
        >
            {/* Tier 2 only. A level change is the one that teaches the two-level
                model, and it is also the one fired from the far side of the
                screen — so it gets a cue with reach, along the whole edge. */}
            <span
                ref={cueRef}
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 z-30 w-0.5 bg-info opacity-0 data-[panel-motion=cue]:animate-panel-cue"
            />
            {isRightSidebarExpanded && !isInSheet && (
                <div
                    className={cn(
                        'group absolute inset-y-0 left-0 z-10 w-1 transition-colors',
                        disabled ? 'cursor-default' : 'cursor-ew-resize hover:bg-info',
                    )}
                    onMouseDown={(e) => {
                        if (!disabled) {
                            onRightSidebarResizeStart(e.clientX);
                        }
                    }}
                >
                    <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <GripVertical className="size-3 text-muted-foreground group-hover:text-info" />
                    </div>
                </div>
            )}
            <div
                className={cn(
                    'flex h-full flex-col transition-opacity duration-200',
                    isRightSidebarExpanded ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
            >
                <div className="border-b border-border px-6 py-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={handleClose}
                            className="size-6 flex-shrink-0 p-0"
                            // `title` alone names it for a mouse and for nothing
                            // else — a screen reader announced this as "button".
                            aria-label="Ocultar painel"
                            title="Ocultar painel"
                        >
                            <ArrowRight2 className="size-4" />
                        </Button>
                        {/* Only the naming half fades — the close button is not
                            part of what changed, and blinking it every time
                            would be noise. */}
                        <div
                            ref={headerRef}
                            className="flex min-w-0 flex-1 items-center gap-2 data-[panel-motion=fade]:animate-panel-fade"
                        >
                            {drilldownScope ? (
                                <InstrumentNameHeading scope={drilldownScope} />
                            ) : (
                                <>
                                    <h2
                                        title={currentStep?.title}
                                        className="min-w-0 truncate text-lg font-semibold text-foreground"
                                    >
                                        {currentStep?.title}
                                    </h2>
                                    {currentStep?.description && !hideStepDescription && (
                                        <TooltipProvider delayDuration={150}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <button
                                                        type="button"
                                                        aria-label="Sobre esta seção"
                                                        className="flex size-4 flex-shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground"
                                                    >
                                                        <InfoCircle className="size-3.5" />
                                                    </button>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side="bottom"
                                                    className="max-w-xs whitespace-normal"
                                                >
                                                    {currentStep.description}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* No `aria-disabled` on the blocked branch: it replaces the
                    step's controls rather than dimming them, and its CTA is the
                    one thing here that must stay actionable. */}
                <div
                    ref={scrollRef}
                    // `overflow-x-hidden` is load-bearing: `overflow-y-auto`
                    // alone computes the other axis to `auto`, so the 8px of
                    // travel would flash a horizontal scrollbar on every swap.
                    className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6"
                    aria-disabled={disabled}
                >
                    {/* The animated wrapper sits inside the scroll container,
                        never on it — translating the scroller itself would drag
                        the scrollbar along with the content. */}
                    <div
                        ref={bodyRef}
                        className="data-[panel-motion=back]:animate-panel-back data-[panel-motion=down]:animate-panel-down data-[panel-motion=fade]:animate-panel-fade data-[panel-motion=fwd]:animate-panel-fwd data-[panel-motion=up]:animate-panel-up"
                    >
                        {/* The way out of the drill-down, named after where it
                            leads. It sits with the content it governs rather
                            than in the header, next to the panel's own hide
                            button — two arrows a few pixels apart, pointing
                            opposite ways, read as one confusing control. */}
                        {drilldownScope && (
                            <button
                                type="button"
                                onClick={() => setPanelScope(null)}
                                className="mb-4 flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                            >
                                <ArrowLeft2 className="size-3" />
                                Voltar para {currentStep?.title}
                            </button>
                        )}

                        {shouldBlockStep ? (
                            <UnsavedReportStepBlock onChoosePatient={handleChoosePatient} />
                        ) : (
                            <div
                                className={cn(
                                    disabled && 'pointer-events-none select-none opacity-60',
                                )}
                            >
                                {CurrentStepComponent && (
                                    <CurrentStepComponent
                                        data={formData}
                                        onUpdate={handleUpdate}
                                        onAddBlock={handleAddBlock}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
